import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { videos, type VideoArticle } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article: VideoArticle | undefined = videos.find((v) => v.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该视频可能已被删除或不存在</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedVideos = videos
    .filter((v) => v.category === article.category && v.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.createdAt,
    image: article.thumbnail,
    url: `https://www.kuaimao.com/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '快猫短视频',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb />

        <article className="w-full bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-8 md:py-12">
            <div className="flex items-center gap-2 mb-4">
              {category && (
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium hover:bg-primary-100 transition-colors whitespace-nowrap"
                >
                  {category.name}
                </Link>
              )}
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-snug">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-background-200/70">
              <div className="flex items-center gap-2">
                <img src={article.authorAvatar} alt={article.author} className="w-8 h-8 rounded-full object-cover" />
                <span className="text-sm font-medium text-foreground-700">{article.author}</span>
              </div>
              <span className="text-xs text-foreground-400">{article.createdAt}</span>
              <div className="flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-eye-line"></i>
                <span>{article.views >= 10000 ? `${(article.views / 10000).toFixed(0)}万` : article.views} 次观看</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-heart-line"></i>
                <span>{article.likes >= 10000 ? `${(article.likes / 10000).toFixed(0)}万` : article.likes} 点赞</span>
              </div>
            </div>

            <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-8 bg-foreground-950">
              <img
                src={article.thumbnail}
                alt={article.title}
                title={`${article.title} - 快猫短视频`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 flex items-center justify-center cursor-pointer hover:bg-primary-500 transition-colors">
                  <i className="ri-play-fill text-2xl md:text-3xl text-background-50 ml-1"></i>
                </div>
              </div>
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70 flex flex-wrap items-center gap-4">
              <span className="text-sm text-foreground-500">分享到：</span>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 hover:text-primary-500 text-foreground-500 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 hover:text-primary-500 text-foreground-500 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 hover:text-primary-500 text-foreground-500 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </a>
            </div>
          </div>
        </article>

        {relatedVideos.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                {relatedVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={`${video.title} - 快猫短视频`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <span className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded-md">
                        {video.duration}
                      </span>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 leading-relaxed group-hover:text-primary-500 transition-colors">
                        {video.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {video.views >= 10000 ? `${(video.views / 10000).toFixed(0)}万` : video.views}
                        </span>
                        <span>{video.author}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}